import {
    ArrowsRightLeftIcon,
    ArrowTopRightOnSquareIcon,
    BanknotesIcon,
    CalendarDaysIcon,
    CheckCircleIcon,
    DocumentIcon,
    DocumentTextIcon,
    ReceiptPercentIcon,
    UserCircleIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import {MoneyLabel} from '../general/MoneyLabel';
import {PrimaryButton} from '../general/PrimaryButton';
import {BankAccountLogo} from '../general/BankAccountLogo';
import {cleanDocumentFileName} from '../../lib/TFICLib';
import {formatDate as formatIsoDate} from '../../lib/utils';
import type {
    MatchBillPartResponse,
    MatchPartsResponse,
    MatchTransactionPartResponse,
} from '../../hooks/useMatchDetails';

interface MatchDetailsViewProps {
    match: MatchPartsResponse;
    currentEntityId?: string;
    onUnmatchPart?: (billIds: string[], transactionIds: string[]) => void;
    onUnmatchAll?: () => void;
    onOpenDocument?: (documentId: string) => void;
}

function signedTransactionAmount(amount: number, flow: string): number {
    return flow === 'outflow' ? -amount : amount;
}

function signedBillAmount(amount: number, direction: string): number {
    return direction === 'purchase_invoice' ? -amount : amount;
}

function billIcon(direction: string) {
    if (direction === 'purchase_invoice') {
        return ReceiptPercentIcon;
    }
    if (direction === 'sales_invoice') {
        return DocumentTextIcon;
    }
    return DocumentIcon;
}

function formatDate(value: string | null): string {
    return value ? formatIsoDate(value) : '-';
}

function matchCurrency(match: MatchPartsResponse): string {
    return match.bill_parts[0]?.currency ?? match.transaction_parts[0]?.currency ?? 'EUR';
}

function creatorLine(match: MatchPartsResponse): string | null {
    if (match.matching_reason === 'RECONCILIATION_ENGINE_RULE') {
        return match.created_by_name
            ? `Automatisch gematcht · regel ${match.created_by_name}`
            : 'Automatisch gematcht';
    }
    return match.created_by_name ? `Aangemaakt door ${match.created_by_name}` : null;
}

function hasDifferenceBooking(match: MatchPartsResponse): boolean {
    return !!match.difference_booking_gla_id || !!match.difference_booking_date;
}

function StatusBadge({match}: {match: MatchPartsResponse}) {
    if (match.is_completed || match.nrs_remainder === 0) {
        return (
            <span className="inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-green-50 px-3 py-1.5 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-200">
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0"/>
                Volledig gematched
            </span>
        );
    }

    return (
        <span className="inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
            <span>Resterend verschil</span>
            <MoneyLabel value={match.nrs_remainder / 100} currency={matchCurrency(match)} showCurrency={true}/>
        </span>
    );
}

function MatchSummary({match}: {match: MatchPartsResponse}) {
    return (
        <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-5">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Match</div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-600">
                        {creatorLine(match) && (
                            <span className="inline-flex items-center gap-1.5">
                                <UserCircleIcon className="h-4 w-4 text-gray-400"/>
                                {creatorLine(match)}
                            </span>
                        )}
                        {match.created_at && (
                            <span className="inline-flex items-center gap-1.5">
                                <CalendarDaysIcon className="h-4 w-4 text-gray-400"/>
                                Aangemaakt op {formatDate(match.created_at)}
                            </span>
                        )}
                        {match.completed_at && (
                            <span className="inline-flex items-center gap-1.5">
                                <CheckCircleIcon className="h-4 w-4 text-gray-400"/>
                                Afgerond op {formatDate(match.completed_at)}
                            </span>
                        )}
                    </div>
                </div>
                <StatusBadge match={match}/>
            </div>

            {hasDifferenceBooking(match) && (
                <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <BanknotesIcon className="h-5 w-5 flex-shrink-0 text-amber-600"/>
                    <div className="text-sm text-amber-800">
                        <div className="font-medium">
                            Verschil weggeboekt
                            {' '}
                            <MoneyLabel
                                value={Math.abs(match.nrs_remainder) / 100}
                                currency={matchCurrency(match)}
                                showCurrency={true}
                            />
                        </div>
                        <div className="mt-0.5 text-amber-700">
                            {match.difference_booking_gla_label ?? match.difference_booking_gla_id}
                            {match.difference_booking_date ? ` · ${formatDate(match.difference_booking_date)}` : ''}
                        </div>
                        {match.difference_booking_reason && (
                            <div className="mt-0.5 italic text-amber-700">“{match.difference_booking_reason}”</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function SectionHeader({title, count}: {title: string; count: number}) {
    return (
        <div className="mb-3 flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 text-xs font-medium text-gray-600">
                {count}
            </span>
        </div>
    );
}

function TransactionRow({
    transaction,
    isCurrent,
    onUnmatch,
}: {
    transaction: MatchTransactionPartResponse;
    isCurrent: boolean;
    onUnmatch?: () => void;
}) {
    return (
        <div
            className={`flex items-center p-4 border rounded-lg transition ${
                isCurrent ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/20' : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
        >
            <div className="flex w-full items-start gap-4">
                <div className="flex flex-1 min-w-0 items-start gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                        <ArrowsRightLeftIcon className="h-5 w-5 text-gray-600"/>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-base font-medium text-gray-900 break-words">
                                {transaction.relation_name || 'N/A'}
                            </span>
                            <span className="whitespace-nowrap flex-shrink-0 text-xs text-gray-500">{formatDate(transaction.date)}</span>
                        </div>
                        {transaction.relation_iban && (
                            <p className="mt-0.5 text-xs text-gray-500">{transaction.relation_iban}</p>
                        )}
                        <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap break-words">
                            {transaction.description}
                        </p>
                    </div>
                </div>

                <div className="flex flex-shrink-0 items-center justify-end gap-4">
                    <div className="border border-gray-200 rounded-md p-2.5">
                        <div className="flex text-center items-center">
                            <BankAccountLogo logoUrl={transaction.bank_logo_url} className="mr-1 size-4 shrink-0"/>
                            <div className="truncate text-xs">{transaction.bank_account_name}</div>
                        </div>
                    </div>

                    <MoneyLabel
                        value={signedTransactionAmount(transaction.amount, transaction.flow) / 100}
                        colorAid={true}
                        currency={transaction.currency}
                        showCurrency={true}
                    />

                    {onUnmatch && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onUnmatch();
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                            aria-label="Ontkoppel transactie"
                        >
                            <XMarkIcon className="w-5 h-5"/>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function BillRow({
    bill,
    isCurrent,
    onUnmatch,
    onOpen,
}: {
    bill: MatchBillPartResponse;
    isCurrent: boolean;
    onUnmatch?: () => void;
    onOpen?: () => void;
}) {
    const Icon = billIcon(bill.direction);
    const clickable = !!onOpen;
    return (
        <div
            onClick={clickable ? onOpen : undefined}
            className={`flex items-center p-4 border rounded-lg transition ${
                isCurrent ? 'border-gray-200 bg-gray-100 opacity-60' : 'border-gray-200 bg-white hover:bg-gray-50'
            } ${clickable ? 'cursor-pointer' : ''}`}
        >
            <div className="flex w-full items-start gap-4">
                <div className="flex flex-1 min-w-0 items-start gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                        <Icon className="h-5 w-5 text-gray-600"/>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-base font-medium text-gray-900 break-words">
                                {bill.relation_name || 'N/A'}
                            </span>
                            <span className="whitespace-nowrap flex-shrink-0 text-xs text-gray-500">{formatDate(bill.date)}</span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600 break-all">
                            {cleanDocumentFileName(bill.file_name)}
                        </p>
                    </div>
                </div>

                <div className="flex flex-shrink-0 items-center justify-end gap-4">
                    <div className="border border-gray-200 rounded-md p-2.5">
                        <div className="truncate text-xs text-gray-700">{bill.reference_identifier || '-'}</div>
                    </div>

                    <MoneyLabel
                        value={signedBillAmount(bill.amount, bill.direction) / 100}
                        colorAid={true}
                        currency={bill.currency}
                        showCurrency={true}
                    />

                    {clickable && (
                        <ArrowTopRightOnSquareIcon className="w-5 h-5 text-gray-400 flex-shrink-0"/>
                    )}

                    {onUnmatch && !isCurrent && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onUnmatch();
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                            aria-label="Ontkoppel document"
                        >
                            <XMarkIcon className="w-5 h-5"/>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export function MatchDetailsView({match, currentEntityId, onUnmatchPart, onUnmatchAll, onOpenDocument}: MatchDetailsViewProps) {
    return (
        <div className="space-y-6">
            <MatchSummary match={match}/>

            <div>
                <SectionHeader title="Gematchte transacties" count={match.transaction_parts.length}/>
                <div className="space-y-3">
                    {match.transaction_parts.length === 0 ? (
                        <div className="text-sm text-gray-500">Geen gematchte transacties</div>
                    ) : (
                        match.transaction_parts.map((transaction) => (
                            <TransactionRow
                                key={transaction.id}
                                transaction={transaction}
                                isCurrent={transaction.id === currentEntityId}
                                onUnmatch={onUnmatchPart ? () => onUnmatchPart([], [transaction.id]) : undefined}
                            />
                        ))
                    )}
                </div>
            </div>

            <div>
                <SectionHeader title="Gematchte documenten" count={match.bill_parts.length}/>
                <div className="space-y-3">
                    {match.bill_parts.length === 0 ? (
                        <div className="text-sm text-gray-500">Geen gematchte documenten</div>
                    ) : (
                        match.bill_parts.map((bill) => (
                            <BillRow
                                key={bill.id}
                                bill={bill}
                                isCurrent={bill.id === currentEntityId || bill.document_id === currentEntityId}
                                onUnmatch={onUnmatchPart ? () => onUnmatchPart([bill.id], []) : undefined}
                                onOpen={onOpenDocument ? () => onOpenDocument(bill.document_id) : undefined}
                            />
                        ))
                    )}
                </div>
            </div>

            {onUnmatchAll && (
                <div className="pt-4">
                    <PrimaryButton
                        title="Alles ontkoppelen"
                        confirmation={{
                            title: 'Wil je deze match ontkoppelen?',
                            description: 'Deze actie kan niet ongedaan worden gemaakt',
                            acceptVerb: 'Ontkoppelen',
                        }}
                        onClick={onUnmatchAll}
                        disabled={match.bill_parts.length === 0 && match.transaction_parts.length === 0}
                    />
                </div>
            )}
        </div>
    );
}
